<script setup lang="ts">
import type { IGetAlbumFilters } from "@/api"
import type { Album, EventVault } from "@/models"
import cover from "@brand-cover"
import { useQuery } from "@tanstack/vue-query"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, onMounted } from "vue"
import { getWelcomeVideo, useGetAlbum, useGetEventVault } from "@/api"
import { FreemiumBanner, GuestBackgroundImage, InstallBanner, MobileHeader, WelcomeVideoModal } from "@/components"
import { Button, ButtonLink, Card, Icon, ReadMore } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useFilters, useGalleryPermissions, useModal } from "@/composables"
import { useTranslation } from "@/composables/useTranslation"
import { GuestLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useTemplateStore } from "@/stores/templateStore"
import { WelcomeVideoModalKey } from "@/symbols"
import { getGuestNavigationHref } from "@/utils"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/welcome", (value: string) => Number(value))

const templateStore = useTemplateStore()
const eventVaultStore = useEventVaultStore()

const { t } = useTranslation()
const { toggle } = useModal(WelcomeVideoModalKey)
const urlParams = useUrlSearchParams<{ album?: number, message?: string }>()
const toast = useToast()

onMounted(() => {
  if (urlParams.message) {
    toast.success({
      title: t("success"),
      description: urlParams.message,
    })
  }
})

const { filters: albumFilters } = useFilters<IGetAlbumFilters>({ include: ["settings", "zip"] })

const { data: eventVault, isLoading: isLoadingEventVault } = useGetEventVault(eventVaultId, { include: ["zip"] })

const { data: currentAlbum, isLoading: isLoadingAlbum } = useGetAlbum(eventVaultId, urlParams.album, albumFilters)

const { isUploadsEnabled, showFreemiumBanner } = useGalleryPermissions(eventVault, currentAlbum, { isHost: () => eventVaultStore.isHost ?? false })

const eventVidType = 3
const { data: welcomeVideoMedia, isLoading: isLoadingWelcomeVideo } = useQuery({
  queryKey: ["getWelcomeVideo", eventVaultId.value, undefined, eventVidType],
  queryFn: () => {
    if (urlParams.album)
      return null
    return getWelcomeVideo(eventVaultId.value)
  },
  initialData: [],
})

const welcomeVideo = computed(() => {
  if (!welcomeVideoMedia.value?.length)
    return
  return welcomeVideoMedia.value?.[0]
})

const displayInfo = computed(() => {
  const source: EventVault | Album | undefined = currentAlbum?.value ?? eventVault?.value

  return {
    coverImageUrl: source?.coverImageUrl ?? "",
    title: source?.title ?? "",
    description: source?.description ?? "",
  }
})

function getDashboardItemHref(page: string, additionalParams: Record<string, string> = {}) {
  if (eventVault.value && eventVault.value.id) {
    return getGuestNavigationHref(page, eventVault.value.id, currentAlbum.value?.id, additionalParams)
  }

  return "#"
}

const isLoading = computed<boolean>(() => {
  return Boolean(isLoadingEventVault.value || isLoadingAlbum.value || isLoadingWelcomeVideo.value)
})

const zipDownloadUrl = computed(() => {
  if (currentAlbum.value) {
    return currentAlbum.value?.zipSummary?.downloadUrl ?? null
  }
  return eventVault.value?.zipSummary?.downloadUrl ?? null
})

const isCustomTemplateSet = computed(() => {
  return Boolean(templateStore.eventTemplate?.id)
})
</script>

<template>
  <FreemiumBanner v-if="showFreemiumBanner" />
  <InstallBanner v-else />

  <GuestLayout>
    <GuestBackgroundImage :is-loading="isLoading" :cover-image="displayInfo.coverImageUrl" />

    <MobileHeader :is-loading="isLoading" :image-src="displayInfo.coverImageUrl" :title="displayInfo.title" />

    <div class="container gpx-dashboard-wrapper mt-3">
      <Card :with-padding="false" class="gpx-dashboard-links-card">
        <h3 class="gpx-dashboard-links-header_desktop">
          {{ t("dashboard_page.menu.title") }}
        </h3>
        <div v-if="isLoading" class="gpx-dashboard-links">
          <div class="gpx-skeleton gpx-link_loading" />
          <div class="gpx-skeleton gpx-link_loading" />
          <div class="gpx-skeleton gpx-link_loading" />
          <div class="gpx-skeleton gpx-link_loading" />
        </div>
        <div v-else class="gpx-dashboard-links">
          <ButtonLink
            v-if="isUploadsEnabled"
            :to="getDashboardItemHref('gallery', currentAlbum || !eventVault?.eventVaultSettings?.hideMainGallery ? { upload: 'true' } : undefined)"
            prefix-icon="ImagePlus"
            variant="secondary"
            size="lg"
            data-test="upload_link"
          >
            {{ eventVault?.eventVaultSettings?.videoEnabled ? t("upload_images_and_videos") : t("upload_images") }}
          </ButtonLink>
          <ButtonLink
            v-if="eventVaultStore.isHost || eventVault?.eventVaultSettings?.sharePixWithGuests"
            :to="getDashboardItemHref('gallery')" prefix-icon="ImageOn" variant="secondary" size="lg"
            data-test="gallery_link"
          >
            {{ t("view_gallery") }}
          </ButtonLink>
          <div class="gpx-dashboard-links_tertiary">
            <ButtonLink
              :to="getDashboardItemHref('reminder')" variant="tertiary"
              prefix-icon="AlarmAlert" class="hidden md:flex" size="lg" data-test="reminder_link"
            >
              {{ t("set_reminder") }}
            </ButtonLink>
            <ButtonLink
              v-if="eventVaultStore.isHost || !eventVault?.eventVaultSettings?.hideGuestbook" :to="getDashboardItemHref('messages')" variant="tertiary" prefix-icon="Edit" size="lg" data-test="guestbook_link"
            >
              {{ t("leave_message") }}
            </ButtonLink>
            <ButtonLink
              v-if="(eventVaultStore.isHost || eventVault?.eventVaultSettings?.allowGuestDownload) && zipDownloadUrl"
              :href="zipDownloadUrl" variant="tertiary" prefix-icon="Download" target="_blank" size="lg"
              data-test="zip_download_link"
            >
              {{ t("dashboard_page.menu.download_zip") }}
            </ButtonLink>
          </div>
          <ButtonLink
            v-if="eventVault?.eventVaultSettings?.invitationEnabled && !urlParams.album"
            :to="`/guest/${eventVaultId}/invitation`" prefix-icon="Envelope" variant="tertiary" size="lg"
            data-test="invitation_link"
          >
            {{ t("dashboard_page.menu.view_invitation") }}
          </ButtonLink>
        </div>
      </Card>
      <div
        class="gpx-dashboard-main-content mt-5 md:mt-0"
        :class="{ 'gpx-dashboard-main-content_custom-template': isCustomTemplateSet }"
      >
        <Card class="gpx-dashboard-message-card" :with-padding="false">
          <h2 class="heading-subtitle mb-4">
            {{ t("message_from_host_title") }}
          </h2>
          <Button
            v-if="welcomeVideo?.thumbnailUrl" v-bind="{ variant: 'flat' }"
            class="gpx-welcome-video-button" @click="toggle"
          >
            <Icon icon="YouTube" />
            {{ t("dashboard_page.welcome_video") }}
          </Button>
          <div v-if="isLoading" class="gpx-skeleton gpx-host-message_loading" />
          <ReadMore v-else-if="displayInfo.description" :content="displayInfo.description" :lines="12" data-test="event_description" />
        </Card>

        <div v-if="isLoading" class="gpx-dashboard-event-image_loading gpx-skeleton" />
        <img
          v-else-if="isCustomTemplateSet" :alt="t('dashboard_page.feature_image_alt')"
          class="gpx-dashboard-event-image rounded-xl"
          :class="{ 'gpx-dashboard-event-image_custom-template': isCustomTemplateSet }"
          :src="displayInfo.coverImageUrl ? displayInfo.coverImageUrl : cover"
        >
        <div v-else class="hidden md:block h-120 relative">
          <img v-if="!isLoading && displayInfo.coverImageUrl" :src="displayInfo.coverImageUrl" class="h-full w-full absolute inset-0 bg-cover bg-center max-w-full object-cover rounded-xl">
          <img v-else :src="cover" class="h-full w-full absolute inset-0 bg-cover bg-center max-w-full object-cover rounded-xl">
        </div>
      </div>
    </div>
  </GuestLayout>

  <WelcomeVideoModal :media="welcomeVideo" />
</template>

<style scoped>
.gpx-dashboard-cover-image {
  object-fit: cover;
  min-width: 100dvw;
  max-height: 25rem;
}

.gpx-dashboard-links-card {
  padding: 1rem 0;
  background-color: transparent;
}

.gpx-dashboard-links {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.heading-subtitle {
  color: var(--template-primary-colour);
}

.gpx-dashboard-links_tertiary {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.gpx-dashboard-links_tertiary a {
  flex: 1;
}

.gpx-dashboard-message-card {
  padding-bottom: 2.5rem;
}

.gpx-dashboard-links-header_desktop {
  color: var(--template-secondary-card-text);
  margin-bottom: 1rem;
}

.gpx-dashboard-links-header_desktop,
.gpx-dashboard-event-image {
  display: none;
}

.gpx-link_loading {
  width: 100%;
  height: 3.375rem;
}

.gpx-host-message_loading {
  width: 100%;
  height: 12rem;
  margin-bottom: 1rem;
}

.gpx-welcome-video-button {
  padding-left: 0;
  padding-bottom: 1.5rem;
}

@media (width >=768px) {
  .gpx-dashboard-wrapper {
    display: flex;
    gap: 1rem;
    margin: 5rem auto 8rem;
  }

  .gpx-dashboard-cover-image {
    z-index: -1;
    inset: 0;
    background-size: cover;
    background-position: center;
    max-width: 100vw;
    object-fit: cover;
    position: fixed;
    max-height: 100dvh;
    height: 100%;
    filter: brightness(0.65) blur(1rem);
    transform: scale(1.1);
  }

  .gpx-dashboard-main-content {
    width: 100%;
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: 1rem;
  }

  .gpx-dashboard-main-content_custom-template {
    display: flex;
    flex-direction: column-reverse;
    gap: 1rem;
  }

  .gpx-dashboard-event-image {
    display: block;
    background-size: cover;
    background-position: center;
    object-fit: cover;
    height: 30rem;
  }

  .gpx-dashboard-event-image_custom-template {
    width: 100%;
    height: auto;
    max-height: 30rem;
  }

  .gpx-dashboard-event-image_loading {
    display: block;
    height: 30rem;
  }

  .gpx-dashboard-message-card {
    padding: 1.5rem;
    min-height: 15rem;
    margin-bottom: auto;
  }

  .gpx-dashboard-links-header_desktop {
    display: block;
  }

  .gpx-dashboard-links-card {
    margin: 0 0 auto;
    padding: 1rem;
    background-color: var(--template-secondary-colour);
    min-width: fit-content;
    min-height: 16rem;
    border: var(--template-border);
  }

  .gpx-dashboard-links-card_disabled-upload-or-gallery,
  .gpx-dashboard-links-card_disabled-upload-and-gallery {
    margin-top: 0;
  }

  .gpx-dashboard-links_tertiary {
    flex-direction: column;
    margin: 0;
  }

  .gpx-dashboard-links a {
    color: var(--template-secondary-card-text);
    font-size: 0.75rem;
    border: none;
    width: fit-content;
    padding: 0;
    gap: 0.5rem;
    background: none;
  }

  .gpx-dashboard-links_tertiary a {
    gap: 0.8rem;
  }

  .gpx-dashboard-links a:hover {
    background-color: transparent;
    text-decoration: underline;
  }

  .gpx-link_loading {
    width: 15rem;
    height: 2.5rem;
  }

  .gpx-host-message_loading {
    width: 100%;
    height: 12rem;
  }
}
</style>
